import { del, get, patch, post } from "../util";

export type PersonPaabudStatusId = "Aabent" | "Lukket" | "Slettet";

export type EfteruddannelseIndberetningStatusId =
  | "Ukendt"
  | "FristOrdinaer"
  | "ModtagetUdenFejl"
  | "ModtagetMedFejl"
  | "Undersoeges"
  | "IndbragtForAdvokatnaevnet"
  | "Henlagt";

export interface PersonPaabudIndberetningLinjeDTO {
  id: string;
  efteruddannelsestypenr: number;
  indberettedeLektioner: number;
}

export interface PersonPaabudIndberetningDTO {
  id: string;
  personPaabudId: string;
  indberetningFrist?: string | null;
  registreringsDato?: string | null;
  signaturNavn?: string | null;
  efteruddannelseIndberetningStatus?: EfteruddannelseIndberetningStatusId | null;
  overfoertTilPersonEfteruddannelseId?: string | null;
  overfoertTilPersonEfteruddannelseIndberetningId?: string | null;
  lektionerOverfoert?: number | null;
  overfoertTilPeriodeFra?: string | null;
  overfoertTilPeriodeTil?: string | null;
  linjer: PersonPaabudIndberetningLinjeDTO[];
}

export interface PersonPaabudDTO {
  id: string;
  personId: string;
  paakraevedeLektioner: number;
  lektionerGennemfoert: number;
  lektionerOverfoert?: number | null;
  periodeFra: string;
  /** Periodens sidste dag, eller null hvis perioden endnu ikke er udløbet. */
  periodeTil: string | null;
  /** Indfrielsesperiodens længde i måneder, som afgørelsen fastsatte den. */
  periodeLaengdeMaaneder?: number | null;
  /** Står forløbet stille, fordi advokaten ikke er udøvende? */
  erIBero: boolean;
  /** Datoen forløbet gik i bero, hvis det står stille netop nu. */
  iBeroSiden?: string | null;
  /** Udøvende dage tilbage på det aktuelle trin — perioden eller fristen. */
  resterendeUdoevendeDage: number;
  kendelseDato?: string | null;
  /** Første kursusdato, der tæller som dokumentation. Null: indfrielsesperiodens start. */
  dokumentationGaelderFra?: string | null;
  bemaerkning?: string | null;
  status: PersonPaabudStatusId;
  decisionDocumentFileName?: string | null;
  decisionDocumentContentType?: string | null;
  opretDato?: string | null;
  opretInit?: string | null;
  retteDato?: string | null;
  retteInit?: string | null;
  indberetning?: PersonPaabudIndberetningDTO | null;
}

export interface CreatePersonPaabudBody {
  paakraevedeLektioner: number;
  /** 6, 12 eller 18. Perioden og fristen beregnes af systemet. */
  periodeLaengdeMaaneder: number;
  kendelseDato: string;
  dokumentationGaelderFra?: string | null;
  decisionDocumentFileName?: string | null;
  decisionDocumentContentType?: string | null;
  bemaerkning?: string | null;
}

export interface UpdatePersonPaabudFields {
  paakraevedeLektioner?: number;
  periodeLaengdeMaaneder?: number;
  kendelseDato?: string | Date | null;
  dokumentationGaelderFra?: string | null;
  decisionDocumentFileName?: string | null;
  decisionDocumentContentType?: string | null;
  bemaerkning?: string | null;
  efteruddannelseIndberetningStatus?: EfteruddannelseIndberetningStatusId | null;
}

export interface JsonPatchOperation {
  op: string;
  path: string;
  value?: unknown;
}

const paabudBasePath = (personId: string) =>
  `person/${encodeURIComponent(personId)}/paabud`;

export const getPersonPaabudList = (
  personId: string,
): Promise<PersonPaabudDTO[]> =>
  get<PersonPaabudDTO[]>({ query: paabudBasePath(personId) });

export const getPersonPaabud = (
  personId: string,
  paabudId: string,
): Promise<PersonPaabudDTO> =>
  get<PersonPaabudDTO>({
    query: `${paabudBasePath(personId)}/${encodeURIComponent(paabudId)}`,
  });

export const createPersonPaabud = (
  personId: string,
  body: CreatePersonPaabudBody,
): Promise<PersonPaabudDTO> =>
  post<PersonPaabudDTO>({ query: paabudBasePath(personId), body });

export const patchPersonPaabud = (
  personId: string,
  paabudId: string,
  operations: JsonPatchOperation[],
): Promise<PersonPaabudDTO> =>
  patch<PersonPaabudDTO>({
    query: `${paabudBasePath(personId)}/${encodeURIComponent(paabudId)}`,
    body: operations,
  });

export const deletePersonPaabud = (
  personId: string,
  paabudId: string,
): Promise<PersonPaabudDTO> =>
  del<PersonPaabudDTO>({
    query: `${paabudBasePath(personId)}/${encodeURIComponent(paabudId)}`,
  }).then((response) => response.data);
